import { MessagingPlatform } from './messagingPlatform.enum';
import { PersonItem } from './person';

export type PersonScope =
  | { kind: 'user'; userId: string }
  | { kind: 'team'; teamId: string };

// apps/api's tsconfig is non-strict, which breaks narrowing on `scope.kind` alone —
// consumers there must use these guards instead of an inline check, the same reason
// isPersonEventActivity/isCommunicationPersonActivity exist in person.ts.
export const isUserScope = (
  scope: PersonScope,
): scope is Extract<PersonScope, { kind: 'user' }> => scope.kind === 'user';

export const isTeamScope = (
  scope: PersonScope,
): scope is Extract<PersonScope, { kind: 'team' }> => scope.kind === 'team';

// Mirrors CRM_KEY_PREFIX.TEAM in apps/api/src/features/crm/utils/personKeys.ts —
// must stay byte-identical, it disambiguates a team-scope personGlobalId from a
// user-scope one on both sides of the wire.
export const TEAM_SCOPE_PREFIX = 'TEAM#';

export interface PersonKey {
  scope: PersonScope;
  personId: string;
}

// The physical key of ANY row in a scope's partition (PERSON#, EMAIL#, a merge
// tombstone, a legacy SHARE#) — unlike PersonKey, which is the logical
// {scope, personId} key of a person row only.
export interface PersonScopeRowKey {
  scopeKey: string;
  sortKey: string;
}

export interface PersonSummary {
  personId: string;
}

export interface CreateManualPersonInput {
  displayName: string;
  emails: string[];
  phones?: string[];
  notes?: string;
  jobTitle?: string;
}

// Every field is optional and absent (or explicit null) means "leave it
// alone" — an empty string or empty array is a real edit that clears the
// attribute. `emails` is absent on purpose: it carries EMAIL# bindings and
// its own concurrency check, so it keeps a dedicated mutation.
export interface UpdatePersonInput {
  displayName?: string;
  jobTitle?: string;
  phones?: string[];
  notes?: string;
}

export interface PersonEmailBindingRef {
  email: string;
  personId: string;
}

// The existing team person a createTeamContactCopy/createPersonForTeam call
// collided with (one shared email is proof it's the same human, so the copy
// is refused rather than merged or overwritten). Distinct from
// PersonEmailConflictError (apps/api/src/utils/errors/crm.ts), which carries
// the same two fields as a thrown error for the single-copy path — this is
// the plain-data shape carried inside CreatePersonForTeamResult's conflict
// branch, where the operation as a whole did not fail.
export interface PersonEmailConflictInfo {
  personId: string;
  email: string;
}

// Result of createPersonForTeam: the personal contact is
// ALWAYS created by the time this resolves — these branches describe only
// whether the team copy alongside it also succeeded. A collision on the copy
// is a genuine third outcome, not a failure of the mutation: modeled as a
// discriminated union rather than `success: boolean` + optional fields so a
// caller can't check the wrong field and assume success.
export type CreatePersonForTeamResult =
  | { kind: 'created'; person: PersonItem; teamPerson: PersonItem }
  | {
      kind: 'teamCopyConflict';
      person: PersonItem;
      conflict: PersonEmailConflictInfo;
    };

// apps/api's tsconfig is non-strict, which breaks narrowing on `.kind` alone —
// consumers there must use this guard instead of an inline check, the same
// reason isUserScope/isTeamScope exist above.
export const isCreatePersonForTeamConflict = (
  result: CreatePersonForTeamResult,
): result is Extract<CreatePersonForTeamResult, { kind: 'teamCopyConflict' }> =>
  result.kind === 'teamCopyConflict';

export interface MergePeopleInput {
  userId: string;
  personIds: string[];
  targetPersonId?: string;
  bindings?: PersonEmailBindingRef[];
}

export interface MergePeopleResult {
  winner: PersonItem;
  loserIds: string[];
}

export interface MergedPersonFields {
  emails: string[];
  origins: MessagingPlatform[] | undefined;
  avatarUrlBySource: Partial<Record<MessagingPlatform, string>>;
  jobTitle?: string;
  notes?: string;
  notesChanged: boolean;
}
